import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export interface ProjectSplitPreview {
  project_id: string | null;
  days: number;
  amount: number;
}

export interface DepreciationLinePreview {
  asset_id: string;
  asset_code: string;
  amount: number;
  book_value_before: number;
  book_value_after: number;
  method_used: string;
  depreciation_method: string | null;
  project_splits?: ProjectSplitPreview[];
  asset_group_code?: string | null;
  expense_account_id?: string | null;
  accum_account_id?: string | null;
  gl_source?: string;
}

export interface DepreciationPreview {
  period_type: string;
  period_start?: string;
  period_end?: string;
  total: number;
  lines: DepreciationLinePreview[];
}

export interface DepreciationRun {
  id: string;
  company_id: string;
  period_type: string;
  period_start: string | null;
  period_end: string | null;
  period_key?: string | null;
  status: string;
  total_depreciation: number;
  journal_voucher_id: string | null;
  notes: string | null;
  created_at?: string;
}

export interface DepreciationLine {
  id: string;
  run_id: string;
  company_id?: string;
  asset_id: string;
  amount: number;
  book_value_before: number | null;
  book_value_after: number | null;
  method_used: string | null;
}

export interface DepreciationProjectSplit {
  id: string;
  line_id: string;
  asset_id: string;
  project_id: string | null;
  days: number;
  amount: number;
}

const assetDepreciationService = {
  async preview(body: {
    period_type: 'monthly' | 'yearly';
    period_start?: string | null;
    period_end?: string | null;
  }): Promise<DepreciationPreview> {
    const res = await api.post('/asset-depreciation/preview', body);
    return unwrap(res);
  },
  async run(body: {
    period_type: 'monthly' | 'yearly';
    expense_account_id?: string | null;
    accum_account_id?: string | null;
    period_start: string;
    period_end: string;
    notes?: string | null;
  }): Promise<{ run: DepreciationRun; journal_voucher_id: string; journal_voucher_number: string }> {
    const res = await api.post('/asset-depreciation/run', body);
    return unwrap(res);
  },
  async listRuns(): Promise<DepreciationRun[]> {
    const res = await api.get('/asset-depreciation/runs');
    const data = unwrap<DepreciationRun[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getRun(id: string): Promise<{
    run: DepreciationRun;
    lines: DepreciationLine[];
    project_splits: DepreciationProjectSplit[];
  }> {
    const res = await api.get(`/asset-depreciation/runs/${id}`);
    return unwrap(res);
  },
};

export default assetDepreciationService;
